'use client'

import { BackdropLoading } from '@/components/backdrop-loading'
import { login, resendVerificationEmail } from '@/lib/api/auth/auth'
import { UserType } from '@/lib/api/common/interfaces/user.interface'
import { ReactFormState, useFormState } from '@/lib/form-state/react-form-state'
import { useAppDispatch, useAuth } from '@/store/hooks'
import { setCredentials } from '@/store/slices/authSlice'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import {
    MdArrowBack,
    MdArrowForward,
    MdCalendarMonth,
    MdCheckCircle,
    MdVisibility,
    MdVisibilityOff,
    MdEmail,
    MdLock,
    MdPayments,
    MdDescription,
    MdSend,
    MdPersonAdd,
    MdClose,
    MdPerson,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { isAiUserType } from '@/lib/auth/restricted-viewer'

/**
 * Conta salva guarda só o que a lista precisa exibir. O refresh token NÃO entra
 * aqui: ele valia 30 dias e ficava no localStorage sem expirar, permitindo
 * entrar sem senha em qualquer sessão do navegador. Escolher a conta agora
 * sempre pede a senha.
 */
interface SavedAccount {
    email: string
    name: string
    picture: string | null
    companyName: string | null
}

const SAVED_ACCOUNTS_KEY = 'saved_accounts'

/**
 * Estilo base de input do login. Sem DaisyUI `input` (que trazia paddings
 * próprios e obrigava dezenas de `!important` para sobrescrever) — Tailwind puro,
 * controle total, foco com anel na cor do tema.
 */
const INPUT_CLASS =
    'w-full h-12 pl-11 pr-4 rounded-xl border border-base-300 bg-base-100 text-[15px] text-base-content placeholder:text-base-content/40 outline-none transition-colors duration-200 focus:border-secondary focus:ring-4 focus:ring-secondary/15'

const PASSWORD_INPUT_CLASS = INPUT_CLASS.replace('pr-4', 'pr-11')

const PRIMARY_BTN_CLASS =
    'w-full h-12 rounded-xl bg-secondary text-primary-content font-semibold text-[15px] transition-all duration-300 hover:brightness-[1.05] hover:shadow-lg hover:shadow-secondary/25 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center gap-2'

function getSavedAccounts(): SavedAccount[] {
    try {
        const data = localStorage.getItem(SAVED_ACCOUNTS_KEY)
        if (!data) return []

        const parsed = JSON.parse(data) as (SavedAccount & {
            refreshToken?: string
        })[]

        // Higiene: apaga o refresh token gravado pelas versões anteriores, que
        // continuaria no disco do usuário mesmo sem ninguém mais lê-lo.
        const hadToken = parsed.some(a => a.refreshToken)
        const accounts = parsed.map(({ refreshToken: _drop, ...rest }) => rest)

        if (hadToken) {
            localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts))
        }

        return accounts
    } catch {
        return []
    }
}

function saveAccount(account: SavedAccount) {
    const accounts = getSavedAccounts().filter(a => a.email !== account.email)
    accounts.unshift(account)
    localStorage.setItem(
        SAVED_ACCOUNTS_KEY,
        JSON.stringify(accounts.slice(0, 10))
    )
}

function removeAccount(email: string) {
    const accounts = getSavedAccounts().filter(a => a.email !== email)
    localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts))
}

export default function LoginPage() {
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [showEmailNotVerified, setShowEmailNotVerified] = useState(false)
    const [userEmail, setUserEmail] = useState('')
    const [isResendingEmail, setIsResendingEmail] = useState(false)
    const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>(() =>
        getSavedAccounts()
    )
    const [selectedAccount, setSelectedAccount] = useState<SavedAccount | null>(
        null
    )
    const [showAccountsList, setShowAccountsList] = useState(
        () => getSavedAccounts().length > 0
    )
    const [passwordValue, setPasswordValue] = useState('')
    const passwordInputRef = useRef<HTMLInputElement>(null)
    const params = useSearchParams()
    const emailVerified = params[0].get('email-verified')

    const dispatch = useAppDispatch()
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }
    }, [isAuthenticated])

    const { formRef, submitButtonRef } = useFormState('login')

    const handleLoginSuccess = (response: any) => {
        const user = response.result.user
        saveAccount({
            email: user.email,
            name: user.name,
            picture: user.picture,
            companyName:
                user.company?.name || user.company?.tradingName || null,
        })

        dispatch(
            setCredentials({
                user,
                token: response.result.access_token,
                refreshToken: response.result.refresh_token ?? '',
                isAuthenticated: true,
                isCompany:
                    user.role.name === 'company' &&
                    user.userType === UserType.COMPANY,
                isCustom: user.userType === UserType.CUSTOM,
                isEmployee:
                    (user.role.name === 'employee' &&
                        user.userType === UserType.EMPLOYEE) ||
                    isAiUserType(user.userType),
                isMaster:
                    user.role.name === 'master' &&
                    user.userType === UserType.MASTER,
                isPartner: user.userType === UserType.PARTNER,
                isProfessional:
                    user.role.name === 'professional' &&
                    user.userType === UserType.PROFESSIONAL,
                accessibleCompanies: user.accessibleCompanies || [],
            })
        )

        setTimeout(() => {
            navigate('/')
        }, 1000)
    }

    const handleLoginError = (response: any, email: string) => {
        if (response.errorCode === 'EmailNotVerified') {
            setUserEmail(email)
            setShowEmailNotVerified(true)
        } else {
            toast.error(
                response.error ||
                    'Ocorreu um erro ao fazer login. Tente novamente.'
            )
            setShowEmailNotVerified(false)
        }
    }

    const handleSubmit = async () => {
        setIsLoading(true)

        const state = ReactFormState.getInstance('login')

        if (!state.isValid) {
            toast.warning('Por favor, preencha todos os campos corretamente.')
            setIsLoading(false)
            return
        }

        const { email, password } = state.values

        const response = await login(email, password)

        if (response.success) {
            setShowEmailNotVerified(false)
            handleLoginSuccess(response)
        } else {
            handleLoginError(response, email)
        }

        setIsLoading(false)
    }

    const handleAccountLogin = async () => {
        if (!selectedAccount || !passwordValue.trim()) {
            toast.warning('Por favor, digite sua senha.')
            return
        }

        setIsLoading(true)

        const response = await login(selectedAccount.email, passwordValue)

        if (response.success) {
            setShowEmailNotVerified(false)
            handleLoginSuccess(response)
        } else {
            handleLoginError(response, selectedAccount.email)
        }

        setIsLoading(false)
    }

    const handleSelectAccount = (account: SavedAccount) => {
        setShowEmailNotVerified(false)
        setSelectedAccount(account)
        setPasswordValue('')
        setTimeout(() => passwordInputRef.current?.focus(), 100)
    }

    const handleRemoveAccount = (e: React.MouseEvent, email: string) => {
        e.stopPropagation()
        e.preventDefault()
        removeAccount(email)
        const remaining = getSavedAccounts()
        setSavedAccounts(remaining)
        if (remaining.length === 0) {
            setShowAccountsList(false)
            setSelectedAccount(null)
        }
    }

    const handleBackToAccounts = () => {
        setSelectedAccount(null)
        setPasswordValue('')
        setShowEmailNotVerified(false)
    }

    const handleUseAnotherAccount = () => {
        setShowAccountsList(false)
        setSelectedAccount(null)
        setPasswordValue('')
        setShowEmailNotVerified(false)
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    const handleResendVerificationEmail = async () => {
        if (!userEmail) return

        setIsResendingEmail(true)
        const response = await resendVerificationEmail(userEmail)

        if (response.success) {
            toast.success(
                'Email de verificação reenviado! Verifique sua caixa de entrada.'
            )
        } else {
            toast.error(
                response.error ||
                    'Erro ao reenviar email. Tente novamente mais tarde.'
            )
        }

        setIsResendingEmail(false)
    }

    // Cabeçalho do painel do formulário — muda conforme o estado.
    const heading =
        showAccountsList && !selectedAccount
            ? 'Escolha sua conta'
            : selectedAccount
              ? `Olá, ${selectedAccount.name.split(' ')[0]}`
              : 'Bem-vindo de volta'
    const subheading =
        showAccountsList && !selectedAccount
            ? 'Selecione com qual conta deseja continuar.'
            : 'Entre para acessar o seu painel.'

    const renderSelectedAccount = () => (
        <div className="space-y-5 animate-fade-in-up">
            <button
                type="button"
                onClick={handleBackToAccounts}
                className="inline-flex items-center gap-1.5 text-sm text-base-content/50 hover:text-base-content transition-colors"
            >
                <MdArrowBack className="w-4 h-4" />
                Voltar
            </button>

            <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary/15 to-secondary/5 flex items-center justify-center overflow-hidden border border-secondary/15 shrink-0">
                    {selectedAccount!.picture ? (
                        <img
                            src={selectedAccount!.picture}
                            alt={selectedAccount!.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <MdPerson className="w-7 h-7 text-secondary/50" />
                    )}
                </div>
                <div className="min-w-0">
                    <p className="font-semibold text-base-content truncate">
                        {selectedAccount!.name}
                    </p>
                    <p className="text-sm text-base-content/50 truncate">
                        {selectedAccount!.email}
                    </p>
                    {selectedAccount!.companyName && (
                        <p className="text-xs text-base-content/40 truncate">
                            {selectedAccount!.companyName}
                        </p>
                    )}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-base-content mb-2">
                    Senha
                </label>
                <div className="relative">
                    <MdLock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                    <input
                        ref={passwordInputRef}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Sua senha"
                        className={PASSWORD_INPUT_CLASS}
                        value={passwordValue}
                        onChange={e => setPasswordValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') handleAccountLogin()
                        }}
                    />
                    <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                    >
                        {showPassword ? (
                            <MdVisibilityOff className="w-5 h-5" />
                        ) : (
                            <MdVisibility className="w-5 h-5" />
                        )}
                    </button>
                </div>
                <div className="text-right mt-2.5">
                    <a
                        href="/auth/forgot-password"
                        className="text-sm text-secondary font-medium hover:underline"
                    >
                        Esqueceu a senha?
                    </a>
                </div>
            </div>

            <button
                type="button"
                onClick={handleAccountLogin}
                className={PRIMARY_BTN_CLASS}
                disabled={isLoading || !passwordValue.trim()}
            >
                {isLoading ? 'Entrando...' : 'Entrar'}
                {!isLoading && <MdArrowForward className="w-5 h-5" />}
            </button>
        </div>
    )

    const renderAccountsPicker = () => (
        <div className="space-y-3 animate-fade-in-up">
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto -mx-1 px-1">
                {savedAccounts.map(account => (
                    <div
                        key={account.email}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelectAccount(account)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleSelectAccount(account)
                            }
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-base-200 hover:border-secondary/30 hover:bg-secondary/5 transition-all group text-left cursor-pointer"
                    >
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-secondary/15 to-secondary/5 flex items-center justify-center overflow-hidden shrink-0 border border-secondary/10">
                            {account.picture ? (
                                <img
                                    src={account.picture}
                                    alt={account.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <MdPerson className="w-6 h-6 text-secondary/50" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-base-content text-sm truncate">
                                {account.name}
                            </p>
                            {account.companyName && (
                                <p className="text-base-content/40 text-xs truncate">
                                    {account.companyName}
                                </p>
                            )}
                        </div>
                        <MdArrowForward className="w-5 h-5 text-base-content/20 group-hover:text-secondary group-hover:translate-x-0.5 transition-all shrink-0" />
                        <button
                            type="button"
                            onClick={e => handleRemoveAccount(e, account.email)}
                            className="text-base-content/30 hover:text-error transition-colors p-1 rounded-lg hover:bg-error/10 shrink-0"
                            title="Remover conta"
                        >
                            <MdClose className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={handleUseAnotherAccount}
                className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed border-base-300 text-base-content/60 hover:text-secondary hover:border-secondary/40 hover:bg-secondary/5 transition-all font-medium text-sm"
            >
                <MdPersonAdd className="w-5 h-5" />
                Entrar em outra conta
            </button>
        </div>
    )

    const renderLoginForm = () => (
        <div className="space-y-5 login-data" ref={formRef}>
            <div>
                <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-base-content mb-2"
                >
                    E-mail
                </label>
                <div className="relative">
                    <MdEmail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                    <input
                        name="email"
                        type="email"
                        placeholder="seu@email.com"
                        className={INPUT_CLASS}
                        required
                        data-validators="email"
                    />
                </div>
            </div>

            <div>
                <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-base-content mb-2"
                >
                    Senha
                </label>
                <div className="relative">
                    <MdLock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                    <input
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Sua senha"
                        className={PASSWORD_INPUT_CLASS}
                        required
                    />
                    <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                    >
                        {showPassword ? (
                            <MdVisibilityOff className="w-5 h-5" />
                        ) : (
                            <MdVisibility className="w-5 h-5" />
                        )}
                    </button>
                </div>
                <div className="text-right mt-2.5">
                    <a
                        href="/auth/forgot-password"
                        className="text-sm text-secondary font-medium hover:underline"
                    >
                        Esqueceu a senha?
                    </a>
                </div>
            </div>

            <button
                type="button"
                onClick={handleSubmit}
                ref={submitButtonRef}
                className={PRIMARY_BTN_CLASS}
                disabled={isLoading}
            >
                {isLoading ? 'Entrando...' : 'Entrar'}
                {!isLoading && <MdArrowForward className="w-5 h-5" />}
            </button>

            {savedAccounts.length > 0 && (
                <button
                    type="button"
                    onClick={() => {
                        setShowAccountsList(true)
                        setSelectedAccount(null)
                    }}
                    className="w-full flex items-center justify-center gap-2 h-11 text-secondary hover:bg-secondary/5 rounded-xl transition-colors font-medium text-sm"
                >
                    <MdPerson className="w-5 h-5" />
                    Usar conta salva
                </button>
            )}
        </div>
    )

    return (
        <section className="min-h-screen flex bg-base-100">
            {/* Painel de marca — escondido no mobile. Gradiente com as cores do
                TEMA (brand-surface), glows flutuantes e a identidade Mediar. */}
            <aside className="hidden lg:flex lg:w-[46%] xl:w-[42%] relative overflow-hidden brand-surface text-primary-content">
                {/* Glows decorativos, suaves e em movimento lento. */}
                <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/20 blur-3xl animate-float-slow" />
                <div
                    className="absolute bottom-[-10%] right-[-5%] w-[28rem] h-[28rem] rounded-full bg-secondary/30 blur-3xl animate-float-slow"
                    style={{ animationDelay: '2.5s' }}
                />
                <div
                    className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full bg-accent/25 blur-3xl animate-float-slow"
                    style={{ animationDelay: '1.2s' }}
                />

                <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
                    <div className="flex items-center gap-3">
                        <img
                            src="/logo512.png"
                            alt="MediarDex"
                            className="w-11 h-11 object-contain drop-shadow"
                        />
                        <span className="text-xl font-bold tracking-tight">
                            MediarDex
                        </span>
                    </div>

                    <div className="max-w-md">
                        <h2 className="text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight">
                            A gestão da sua empresa, clínica, hospital, tudo em
                            um só lugar.
                        </h2>
                        <p className="mt-5 text-primary-content/80 text-lg leading-relaxed">
                            Agenda, prontuário, atendimento e financeiro
                            integrados — do primeiro contato ao pagamento.
                        </p>

                        <div className="mt-10 space-y-4">
                            {[
                                {
                                    icon: (
                                        <MdCalendarMonth className="w-5 h-5" />
                                    ),
                                    label: 'Agendamento e prontuário conectados',
                                },
                                {
                                    icon: <MdDescription className="w-5 h-5" />,
                                    label: 'Documentos, guias e evoluções num clique',
                                },
                                {
                                    icon: <MdPayments className="w-5 h-5" />,
                                    label: 'Cobrança e financeiro sem retrabalho',
                                },
                            ].map((f, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-3"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
                                        {f.icon}
                                    </div>
                                    <span className="text-primary-content/90">
                                        {f.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <p className="text-primary-content/60 text-sm">
                        © {new Date().getFullYear()} MediarDex
                    </p>
                </div>
            </aside>

            {/* Painel do formulário. */}
            <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-10">
                <div className="w-full max-w-[400px] animate-fade-in-up">
                    {/* Marca no topo (mobile — o painel esquerdo some). */}
                    <div className="flex lg:hidden items-center gap-2.5 mb-8">
                        <img
                            src="/logo512.png"
                            alt="MediarDex"
                            className="w-9 h-9 object-contain"
                        />
                        <span className="text-lg font-bold text-secondary tracking-tight">
                            MediarDex
                        </span>
                    </div>

                    <div className="mb-7">
                        <h1 className="text-[28px] font-bold text-base-content tracking-tight">
                            {heading}
                        </h1>
                        <p className="text-base-content/50 mt-1.5">
                            {subheading}
                        </p>
                    </div>

                    {emailVerified && (
                        <div className="mb-5 rounded-xl bg-success/10 border border-success/20 p-4 flex items-center gap-3 animate-scale-in">
                            <MdCheckCircle className="text-xl text-success shrink-0" />
                            <p className="text-sm text-base-content/80">
                                E-mail verificado com sucesso! Entre na sua
                                conta.
                            </p>
                        </div>
                    )}

                    {showEmailNotVerified && (
                        <div className="mb-5 rounded-xl bg-warning/10 border border-warning/25 p-4 animate-scale-in">
                            <div className="flex items-start gap-3 mb-3">
                                <MdEmail className="text-xl text-warning shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-base-content mb-0.5">
                                        E-mail não verificado
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        Verifique seu e-mail antes de entrar.
                                        Não recebeu?
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleResendVerificationEmail}
                                disabled={isResendingEmail}
                                className="w-full h-10 rounded-lg border border-warning/40 text-warning-content bg-warning/10 hover:bg-warning/20 transition-colors text-xs font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isResendingEmail ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs" />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <MdSend className="text-base" />
                                        Reenviar e-mail de verificação
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {showAccountsList
                        ? selectedAccount
                            ? renderSelectedAccount()
                            : renderAccountsPicker()
                        : renderLoginForm()}
                </div>
            </div>

            <BackdropLoading visible={isLoading} />
        </section>
    )
}
